import { PeekBox, PeekButton, PeekStack, PeekToggleButton, PeekToggleButtonGroup, PeekTextField, PeekSelect, PeekMenuItem, PeekTypography, PeekSlider, } from "@piiqtechnologies/ui-components";
import React, { useEffect, useState, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import { useIntl } from "react-intl";
import { Plus, Minus } from "@piiqtechnologies/svg-icons";
import { useCanvasSetup } from "./useCanvasSetup";
import { useToolHandlers } from "./useToolHandlers";
import { useDrawingEvents } from "./useDrawingEvents";
import { useCanvasActions } from "./useCanvasActions";
import { useKeyboardShortcuts } from "./useKeyboardShortcuts";
import ColorPicker from "./ColorPicker";
import messages from "./messages.i18n";
export const FloorplanEditor = ({ imageUrl, onSave, onCancel }) => {
    const { svgCanvasRef, svgCanvasInstanceRef } = useCanvasSetup(imageUrl);
    const [tool, setTool] = useState("select");
    const [brushWidth, setBrushWidth] = useState(3);
    const [wallWidth, setWallWidth] = useState(3);
    const [textValue, setTextValue] = useState("");
    const theme = useTheme();
    const intl = useIntl();
    const { handleAddWall, handleAddDoor, handleAddWindow, handleAddText, handleAddTextAtPosition, handleAddRectangle, handleDelete, } = useToolHandlers({
        svgCanvasInstanceRef,
        setTool,
        wallWidth,
        textValue,
        setTextValue,
    });
    useDrawingEvents({
        svgCanvasRef,
        svgCanvasInstanceRef,
        tool,
        brushWidth,
        handleAddWall,
        handleAddDoor,
        handleAddWindow,
        handleAddTextAtPosition,
        textValue,
    });
    const [zoom, setZoom] = useState(100);
    const [fillColor, setFillColor] = useState("#000000");
    const [strokeWidth, setStrokeWidth] = useState(3);
    const { handleSave, handleDownload, handleZoomIn, handleZoomOut, handleSetZoom, handleSetFillColor, handleSetStrokeWidth, } = useCanvasActions({
        svgCanvasInstanceRef,
        svgCanvasRef,
        onSave,
    });
    useKeyboardShortcuts({
        svgCanvasInstanceRef,
        svgCanvasRef,
        onDelete: handleDelete,
        moveStep: 5,
    });
    const zoomOptions = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 125, 150, 175, 200, 250, 300];
    const handleZoomChange = useCallback((newZoom) => {
        setZoom(newZoom);
        handleSetZoom(newZoom);
    }, [handleSetZoom]);
    const handleZoomInClick = useCallback(() => {
        const currentIndex = zoomOptions.indexOf(zoom);
        if (currentIndex < zoomOptions.length - 1) {
            handleZoomChange(zoomOptions[currentIndex + 1]);
        }
        else {
            handleZoomIn();
        }
    }, [zoom, zoomOptions, handleZoomChange, handleZoomIn]);
    const handleZoomOutClick = useCallback(() => {
        const currentIndex = zoomOptions.indexOf(zoom);
        if (currentIndex > 0) {
            handleZoomChange(zoomOptions[currentIndex - 1]);
        }
        else {
            handleZoomOut();
        }
    }, [zoom, zoomOptions, handleZoomChange, handleZoomOut]);
    useEffect(() => {
        const canvas = svgCanvasInstanceRef.current;
        if (!canvas)
            return;
        const currentZoomPercent = Math.round(canvas.getZoom() * 100);
        if (currentZoomPercent !== zoom && zoomOptions.includes(currentZoomPercent)) {
            setZoom(currentZoomPercent);
        }
        canvas.setMode("select");
    }, [tool, svgCanvasInstanceRef, zoom, zoomOptions]);
    useEffect(() => {
        const handleSelectionChange = (event) => {
            var _a;
            const canvas = svgCanvasInstanceRef.current;
            if (!canvas)
                return;
            const selectedElements = ((_a = event.detail) === null || _a === void 0 ? void 0 : _a.elements) || [];
            if (selectedElements.length > 0) {
                // Get the first selected element (excluding SVG root)
                const element = selectedElements.find((elem) => { var _a; return ((_a = elem.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== "svg"; }) || selectedElements[0];
                // Get original values (before highlight was applied)
                const originalStroke = element.getAttribute("data-original-stroke");
                const originalStrokeWidth = element.getAttribute("data-original-stroke-width");
                const elementFill = element.getAttribute("fill");
                if (elementFill && elementFill !== "none") {
                    setFillColor(elementFill.startsWith("#") ? elementFill : `#${elementFill}`);
                }
                else if (elementFill === "none") {
                    setFillColor("none");
                }
                const elementStrokeWidth = originalStrokeWidth || element.getAttribute("stroke-width");
                if (elementStrokeWidth) {
                    const width = parseFloat(elementStrokeWidth);
                    if (!isNaN(width)) {
                        setStrokeWidth(width);
                    }
                }
            }
        };
        document.addEventListener("selectionchanged", handleSelectionChange);
        return () => {
            document.removeEventListener("selectionchanged", handleSelectionChange);
        };
    }, [svgCanvasInstanceRef]);
    const handleToolChange = useCallback((newTool) => {
        setTool(newTool);
    }, []);
    return (React.createElement(PeekStack, { spacing: 2, sx: { width: "100%", height: "100%", display: "flex", flexDirection: "column" } },
        React.createElement(PeekStack, { direction: "row", spacing: 2, alignItems: "center", sx: {
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                flexWrap: "wrap",
                justifyContent: "space-between",
            } },
            React.createElement(PeekStack, { direction: "row", spacing: 1, alignItems: "center", sx: { flexWrap: "wrap", flexGrow: 1 } },
                React.createElement(PeekToggleButtonGroup, { value: tool, exclusive: true, onChange: (_, newTool) => newTool && handleToolChange(newTool), "aria-label": "drawing tool", sx: {
                        "& .MuiToggleButton-root.Mui-selected": {
                            backgroundColor: theme.palette.teal[500],
                            color: theme.palette.common.white,
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: theme.palette.teal[600],
                            },
                        },
                        "& .MuiToggleButton-root:not(.Mui-selected)": {
                            color: theme.palette.text.primary,
                        },
                    } },
                    React.createElement(PeekToggleButton, { value: "select", "aria-label": "select" }, intl.formatMessage(messages.select)),
                    React.createElement(PeekToggleButton, { value: "wall", "aria-label": "wall" }, intl.formatMessage(messages.wall)),
                    React.createElement(PeekToggleButton, { value: "door", "aria-label": "door" }, intl.formatMessage(messages.door)),
                    React.createElement(PeekToggleButton, { value: "window", "aria-label": "window" }, intl.formatMessage(messages.window)),
                    React.createElement(PeekToggleButton, { value: "draw", "aria-label": "draw" }, intl.formatMessage(messages.draw)),
                    React.createElement(PeekToggleButton, { value: "text", "aria-label": "text" }, intl.formatMessage(messages.text)),
                    React.createElement(PeekToggleButton, { value: "rectangle", "aria-label": "rectangle" }, intl.formatMessage(messages.rectangle))),
                React.createElement(PeekStack, { direction: "row", spacing: 1, alignItems: "center", sx: { ml: 2 } },
                    React.createElement(ColorPicker, { value: fillColor, onChange: (color) => {
                            setFillColor(color);
                            handleSetFillColor(color);
                        } }),
                    React.createElement(PeekStack, { direction: "row", spacing: 1, alignItems: "center" },
                        React.createElement(PeekTypography, { variant: "body2", sx: { fontSize: "0.875rem", whiteSpace: "nowrap" } }, intl.formatMessage(messages.thickness)),
                        React.createElement(PeekBox, { sx: { width: "100px" } },
                            React.createElement(PeekSlider, { value: strokeWidth, onChange: (_, value) => {
                                    const width = Array.isArray(value) ? value[0] : value;
                                    setStrokeWidth(width);
                                    handleSetStrokeWidth(width);
                                }, min: 1, max: 50, size: "small" })),
                        React.createElement(PeekTypography, { variant: "body2", sx: { fontSize: "0.875rem", minWidth: "40px" } },
                            strokeWidth,
                            "px"))),
                React.createElement(PeekStack, { direction: "row", spacing: 0.5, alignItems: "center", sx: { ml: 2 } },
                    React.createElement(PeekBox, { onClick: handleZoomOutClick, sx: {
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0.5,
                            "&:hover": {
                                opacity: 0.7,
                            },
                        }, "aria-label": intl.formatMessage(messages.zoomOut) },
                        React.createElement(Minus, { width: 14, height: 14 })),
                    React.createElement(PeekSelect, { value: zoom, onChange: (e) => handleZoomChange(Number(e.target.value)), size: "small", sx: {
                            minWidth: 90,
                            backgroundColor: theme.palette.common.white,
                            color: theme.palette.text.primary,
                            "& .MuiSelect-select": {
                                color: theme.palette.text.primary,
                            },
                        } }, zoomOptions.map((option) => (React.createElement(PeekMenuItem, { key: option, value: option },
                        option,
                        "%")))),
                    React.createElement(PeekBox, { onClick: handleZoomInClick, sx: {
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0.5,
                            "&:hover": {
                                opacity: 0.7,
                            },
                        }, "aria-label": intl.formatMessage(messages.zoomIn) },
                        React.createElement(Plus, { width: 14, height: 14 })))),
            tool === "text" && (React.createElement(PeekStack, { direction: "row", spacing: 1, alignItems: "center" },
                React.createElement(PeekTextField, { size: "small", placeholder: intl.formatMessage(messages.enterText), value: textValue, onChange: (e) => setTextValue(e.target.value), onKeyPress: (e) => {
                        if (e.key === "Enter") {
                            handleAddText();
                        }
                    } }),
                React.createElement(PeekButton, { variant: "outlined", size: "small", onClick: handleAddText }, intl.formatMessage(messages.addText)))),
            tool === "wall" && (React.createElement(PeekStack, { direction: "row", spacing: 1, alignItems: "center" },
                React.createElement(PeekTypography, { variant: "body2", sx: { fontSize: "0.875rem", whiteSpace: "nowrap" } }, intl.formatMessage(messages.wallWidth)),
                React.createElement(PeekBox, { sx: { width: "100px" } },
                    React.createElement(PeekSlider, { value: wallWidth, onChange: (_, value) => {
                            const width = Array.isArray(value) ? value[0] : value;
                            setWallWidth(width);
                        }, min: 3, max: 30, size: "small" })),
                React.createElement(PeekTypography, { variant: "body2", sx: { fontSize: "0.875rem", minWidth: "40px" } },
                    wallWidth,
                    "px"))),
            tool === "draw" && (React.createElement(PeekStack, { direction: "row", spacing: 1, alignItems: "center" },
                React.createElement(PeekTypography, { variant: "body2", sx: { fontSize: "0.875rem", whiteSpace: "nowrap" } }, intl.formatMessage(messages.brushWidth)),
                React.createElement(PeekBox, { sx: { width: "100px" } },
                    React.createElement(PeekSlider, { value: brushWidth, onChange: (_, value) => {
                            const width = Array.isArray(value) ? value[0] : value;
                            setBrushWidth(width);
                        }, min: 1, max: 50, size: "small" })),
                React.createElement(PeekTypography, { variant: "body2", sx: { fontSize: "0.875rem", minWidth: "40px" } },
                    brushWidth,
                    "px"))),
            tool === "door" && (React.createElement(PeekBox, { sx: { color: theme.palette.text.secondary, fontSize: "0.875rem" } }, intl.formatMessage(messages.clickToPlaceDoor))),
            tool === "window" && (React.createElement(PeekBox, { sx: { color: theme.palette.text.secondary, fontSize: "0.875rem" } }, intl.formatMessage(messages.clickToPlaceWindow))),
            tool === "rectangle" && (React.createElement(PeekButton, { variant: "outlined", size: "small", onClick: handleAddRectangle }, intl.formatMessage(messages.addRectangle))),
            React.createElement(PeekStack, { direction: "row", spacing: 1, sx: { ml: "auto" }, alignItems: "center" },
                React.createElement(PeekButton, { variant: "contained", size: "small", onClick: handleDelete, disabled: tool !== "select" }, intl.formatMessage(messages.deleteSelected)),
                React.createElement(PeekButton, { variant: "contained", onClick: handleSave }, intl.formatMessage(messages.save)),
                React.createElement(PeekButton, { variant: "contained", onClick: handleDownload }, intl.formatMessage(messages.download)),
                React.createElement(PeekButton, { variant: "contained", onClick: onCancel }, intl.formatMessage(messages.cancel)))),
        React.createElement(PeekBox, { sx: {
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "auto",
                backgroundColor: theme.palette.grey[100],
                p: 2,
            } },
            React.createElement(PeekBox, { ref: svgCanvasRef, sx: {
                    width: "800px",
                    height: "600px",
                    position: "relative",
                    backgroundColor: "#ffffff",
                    cursor: tool === "select" ? "default" : "crosshair",
                    outline: "none",
                    "& svg": {
                        "& *:not(image)": {
                            cursor: tool === "select" ? "pointer" : "crosshair",
                            "&:hover": {
                                filter: "drop-shadow(0 0 2px rgba(4, 210, 198, 0.5))",
                            },
                        },
                    },
                }, tabIndex: 0 }))));
};
export default FloorplanEditor;
